import { useCallback, useMemo, useRef } from 'react';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import {
  useCreateVenuaPlannerProject,
  useUpdateVenuaPlannerProject,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import {
  buildProjectFormSchema,
  emptyProjectFormValues,
  type ProjectFormValues,
} from './projectFormSchema';
import { toProjectRequestBody } from './toProjectRequest';

/** Mapování názvů polí z BE validace na cesty polí ve formuláři. */
const SERVER_FIELD_MAP: ServerFieldMap = {
  Name: 'name',
  DestinationIds: 'destinations',
};

interface UseProjectFormOptions {
  /** Bez `projectId` = zakládání, s `projectId` = editace. */
  projectId?: string;
  /** Výchozí hodnoty (pro editaci namapované z detailu projektu). */
  initialValues?: ProjectFormValues;
  /** Voláno po úspěšném uložení — invalidace, toast; NEzavírá okno (to řeší „Uložit & zpět"). */
  onSaved: () => void;
  /** Voláno při chybě, kterou se nepodařilo namapovat na konkrétní pole (např. síťová chyba). */
  onError?: (error: unknown) => void;
}

/**
 * Zapouzdřuje formulářovou logiku projektu (sdílená pro zakládání i editaci) – TanStack Form
 * se Zod validací, create/update mutace a promítnutí serverových validačních chyb do polí.
 */
export function useProjectForm({
  projectId,
  initialValues,
  onSaved,
  onError,
}: UseProjectFormOptions) {
  const { t } = useTranslation('venua-planner');
  const schema = useMemo(() => buildProjectFormSchema(t), [t]);

  const createMutation = useCreateVenuaPlannerProject();
  const updateMutation = useUpdateVenuaPlannerProject();
  const successRef = useRef(false);

  const form = useForm({
    defaultValues: initialValues ?? emptyProjectFormValues(),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      successRef.current = false;
      const data = toProjectRequestBody(value);
      try {
        if (projectId) {
          await updateMutation.mutateAsync({ projectId, data });
        } else {
          await createMutation.mutateAsync({ data });
        }
        onSaved();
        successRef.current = true;
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  /** Spustí validaci + uložení; vrací `true` při úspěchu (pro „Uložit & zpět"). */
  const submit = useCallback(async () => {
    await form.handleSubmit();
    return successRef.current;
  }, [form]);

  return {
    form,
    submit,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
  };
}
